import ZionForm, { useZionForm } from '@fs/zion-form'
import { H6, Separator, Subheading, useOverlay } from '@fs/zion-ui'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getMostRecentFieldValue,
  getNestedOrFloatingElementById,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import HighlightButtonOld from '../../../../../components/generic/panels/shared/HighlightButtonOld'
import UnsavedChangesOverlay from '../../../../../components/generic/panels/shared/UnsavedChangesOverlay'
import { useR2StateContext } from '../../../../../providers/r2Provider/R2Context'
import useUnsavedChangesOverlay from '../../../../../hooks/useUnsavedChangesOverlay'
import { addPlaceAndDateFieldsToFormDefaults } from '../../../../../../shared/utils/utils'
import { useOverlayContext } from '../../../../../../shared/providers/overlayContextProvider/OverlayContext'
import EditPersonFormElement from '../EditPersonFormElement'
import PanelHeader from '../PanelHeader'
import { useSharedBrowserStateContext } from '../../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

const SharedFieldPage1 = ({ handleSubmit, onDiscard }) => {
  const state = useSlsStateContext()
  const r2State = useR2StateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const { editSharedFieldPage1Overlay, highlightsPanelOverlay, topMostPanel } = useOverlayContext()
  const [t] = useTranslation()
  const h = useSlsHighlightsContext()
  const unsavedChangesOverlay = useOverlay()
  const sharedField = getNestedOrFloatingElementById(state.images, r2State.editingSharedFieldId)
  const fieldDisplayValue = getMostRecentFieldValue(sharedField)
  const heading = fieldDisplayValue
    ? t('edit-shared-field.heading', '{fieldType}: {fieldValue}', {
        fieldType: sharedField?.typeData?.displayLabel,
        fieldValue: fieldDisplayValue,
      })
    : ''

  const formDefaultValues = useMemo(() => {
    const defaultValues = {}
    if (sharedField) {
      addPlaceAndDateFieldsToFormDefaults(defaultValues, [sharedField])
    }

    return defaultValues
  }, [sharedField])

  const methods = useZionForm({
    mode: 'all',
    defaultValues: formDefaultValues,
  })

  useEffect(() => {
    if (
      editSharedFieldPage1Overlay.isOpen &&
      topMostPanel() === 'editSharedFieldPage1' &&
      !highlightsPanelOverlay.isOpen
    ) {
      const splitScreenImageArk = thumbnails?.[r2State.referenceFocusedIndex]?.ark
      const elementIds = [r2State.editingSharedFieldId]

      h.setDisplayHighlights({
        mainPartitionKey: state.currentImageArk,
        elementIds,
      })

      if (r2State.splitScreen && splitScreenImageArk) {
        h.setDisplayHighlights({
          splitScreenPartitionKey: splitScreenImageArk,
          elementIds,
        })
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editSharedFieldPage1Overlay.isOpen,
    topMostPanel,
    highlightsPanelOverlay.isOpen,
    r2State.editingSharedFieldId,
    r2State.referenceFocusedIndex,
    r2State.splitScreen,
    state.currentImageArk,
    thumbnails,
  ])

  useUnsavedChangesOverlay({
    overlay: unsavedChangesOverlay,
    methods,
    panelId: 'editSharedFieldPage1',
  })

  return (
    <>
      <ZionForm onSubmit={handleSubmit} methods={methods} id="edit-shared-field">
        <PanelHeader
          heading={heading}
          caption={t('edit-shared-field.instructions.heading', 'Fix any mistakes that you see.')}
          headingSize="H5"
        />
        <Separator />
        <H6>
          {t('edit-shared-field.edit-field.heading', '1. Edit the {fieldType} field.', {
            fieldType: sharedField?.typeData?.displayLabel,
          })}
        </H6>
        {sharedField && (
          <EditPersonFormElement
            {...{
              field: sharedField,
              r2State,
              showEditHighlight: false,
              enableLockSharedField: false,
            }}
          />
        )}
        <Separator size="xs" />
        <H6>
          {t('edit-shared-field.edit-highlight.heading', '2. Edit the highlight.')}
          <Subheading>{t('', 'Match the highlight to the transcription.')}</Subheading>
        </H6>
        <Separator size="xs" />
        <HighlightButtonOld field={sharedField} />
      </ZionForm>
      <UnsavedChangesOverlay
        formId="edit-shared-field"
        onDiscard={onDiscard}
        overlay={unsavedChangesOverlay}
        alertMode
      />
    </>
  )
}

export default SharedFieldPage1
